"use client"

import type React from "react"
import { Sidebar } from "./sidebar"
import { TopBar } from "./top-bar"
import { useSidebarStore } from "@/lib/stores/sidebar-store"
import { cn } from "@/lib/utils"
import { X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useEffect } from "react"
import { ErrorBoundary } from "@/components/error-boundary"
import { Logo } from "@/components/ui/logo"
import { usePathname } from "next/navigation"

interface AppShellProps {
  children: React.ReactNode
}

export function AppShell({ children }: AppShellProps) {
  const { leftSidebarOpen, closeLeftSidebar, closeAll } = useSidebarStore()
  const pathname = usePathname()
  const isAdvisorLikeRoute =
    pathname?.startsWith("/advisor") ||
    pathname?.startsWith("/brain") ||
    pathname?.startsWith("/dashboards") ||
    pathname?.startsWith("/numbers") ||
    pathname?.startsWith("/goals")

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeAll()
    }
    window.addEventListener("keydown", handleEscape)
    return () => window.removeEventListener("keydown", handleEscape)
  }, [closeAll])

  return (
    <div className="flex h-screen bg-background">
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      {/* Desktop Sidebar - Full height */}
      <div className="hidden lg:block h-full">
        <Sidebar />
      </div>

      {/* Mobile Sidebar Overlay */}
      {leftSidebarOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={closeLeftSidebar} aria-hidden="true" />
          <div
            className={cn(
              "fixed inset-y-0 left-0 z-50 w-72 bg-background lg:hidden",
              "transform transition-transform duration-300 ease-out",
              leftSidebarOpen ? "translate-x-0" : "-translate-x-full",
            )}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
          >
            <div className="flex h-14 items-center justify-between px-4">
              <Logo width={90} height={24} />
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={closeLeftSidebar}
                aria-label="Close navigation menu"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
            <Sidebar isMobile />
          </div>
        </>
      )}

      {/* Main content area - TopBar conditionally hidden for Advisor-like routes */}
      <div className="flex flex-1 flex-col overflow-hidden min-w-0">
        {!isAdvisorLikeRoute && <TopBar />}
        <main id="main-content" className="flex-1 overflow-hidden" role="main">
          <ErrorBoundary>{children}</ErrorBoundary>
        </main>
      </div>
    </div>
  )
}
